// askSmart-ui/src/pages/MapleCoin/operations/AuditTab.tsx
//
// The Admin access audit (Stage 8's customer_access_event): who opened which
// customer's record, when. One view answers "who accessed this customer?",
// "which customers did this Admin access?" and "what access happened in this
// period?". The records are immutable; this view only reads them.
import { FormEvent, useCallback, useContext, useEffect, useState } from 'react';
import { AccessEvent, AccessFilter, listAccessEvents } from './opsApi';
import { Busy, Identity, Loading, Panel, PeopleContext, Unavailable, dateTime, dayStart, titleCase } from './shared';
import { OpsNav } from './MapleCoinOperations';

type Form = { from: string; to: string; actorSub: string; customerNumber: string; accountNumber: string; action: string; channel: string };
const EMPTY: Form = { from: '', to: '', actorSub: '', customerNumber: '', accountNumber: '', action: '', channel: '' };

const toFilter = (f: Form): AccessFilter => ({
  from: dayStart(f.from), to: dayStart(f.to, true), actorSub: f.actorSub.trim() || undefined,
  customerNumber: f.customerNumber.trim() || undefined, accountNumber: f.accountNumber.trim() || undefined,
  action: f.action || undefined, channel: f.channel || undefined,
});

export default function AuditTab({ nav }: { nav: OpsNav }) {
  const [form, setForm] = useState<Form>(EMPTY);
  const [applied, setApplied] = useState<AccessFilter>({});
  const [events, setEvents] = useState<AccessEvent[]>([]);
  const [admins, setAdmins] = useState<{ sub: string; events: number }[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'invalid'>('loading');
  const [more, setMore] = useState(false);
  const people = useContext(PeopleContext);

  const load = useCallback(async (filter: AccessFilter, before?: string) => {
    if (before) setMore(true); else setState('loading');
    try {
      const r = await listAccessEvents({ ...filter, before });
      setEvents((prev) => (before ? [...prev, ...r.events] : r.events));
      setAdmins(r.admins);
      setActions(r.actions);
      setNext(r.nextBefore);
      setState('ready');
    } catch (err: any) {
      setState(err?.status === 400 ? 'invalid' : 'error');
    } finally {
      setMore(false);
    }
  }, []);
  useEffect(() => { load({}); }, [load]);

  const apply = (e?: FormEvent) => {
    e?.preventDefault();
    const f = toFilter(form);
    setApplied(f);
    load(f);
  };
  const set = (k: keyof Form) => (e: { target: { value: string } }) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div className="mco-stack">
      <Panel title="Access audit" label="Access audit">
        <p className="mco-note">Every time MapleCoin Admin or Operations opens an individual customer, account, transaction or journal, it is recorded here. Records are permanent and cannot be changed.</p>
        <form className="mco-filters" onSubmit={apply} aria-label="Filter access records">
          <label>From<input type="date" value={form.from} onChange={set('from')} /></label>
          <label>To<input type="date" value={form.to} onChange={set('to')} /></label>
          <label>Admin
            <select value={form.actorSub} onChange={set('actorSub')}>
              <option value="">Any Admin</option>
              {admins.map((a) => <option key={a.sub} value={a.sub}>{people[a.sub]?.name ?? people[a.sub]?.email ?? a.sub} ({a.events})</option>)}
            </select>
          </label>
          <label>Customer number<input value={form.customerNumber} onChange={set('customerNumber')} placeholder="C…" /></label>
          <label>Account number<input value={form.accountNumber} onChange={set('accountNumber')} inputMode="numeric" /></label>
          <label>Action
            <select value={form.action} onChange={set('action')}>
              <option value="">Any action</option>
              {actions.map((a) => <option key={a} value={a}>{titleCase(a)}</option>)}
            </select>
          </label>
          <label>Channel
            <select value={form.channel} onChange={set('channel')}>
              <option value="">Admin and Operations</option>
              <option value="ADMIN">Admin (customer servicing)</option>
              <option value="OPERATIONS">Operations</option>
            </select>
          </label>
          <div className="mco-filter-actions">
            <button type="submit" className="mco-btn">Apply</button>
            <button type="button" className="mco-btn mco-btn--quiet" onClick={() => { setForm(EMPTY); setApplied({}); load({}); }}>Clear</button>
          </div>
        </form>
      </Panel>

      <Panel label="Access records">
        {state === 'loading' && <Loading />}
        {state === 'error' && <Unavailable onRetry={() => load(applied)} />}
        {state === 'invalid' && <p className="mco-error" role="alert">One of the filters is not valid.</p>}
        {state === 'ready' && (
          <>
            <div className="mco-table-wrap">
              <table className="mco-table">
                <thead><tr><th>Time</th><th>Who</th><th>Channel</th><th>Action</th><th>Customer</th><th>Account</th><th>Transaction</th><th>Request ID</th></tr></thead>
                <tbody>
                  {events.length === 0 && <tr><td colSpan={8} className="mco-empty">No access records match.</td></tr>}
                  {events.map((e) => (
                    <tr key={e.eventSeq}>
                      <td className="mco-nowrap">{dateTime(e.occurredAt)}</td>
                      <td><Identity sub={e.actorSub} /></td>
                      <td>{e.channel === 'OPERATIONS' ? 'Operations' : 'Admin'}</td>
                      <td>{titleCase(e.action)}</td>
                      <td><code>{e.customerNumber}</code></td>
                      <td>{e.accountNumber ? <code>{e.accountNumber}</code> : '—'}</td>
                      <td>{e.transactionReference
                        ? <button type="button" className="mco-link" onClick={() => nav.openTransaction(e.transactionReference!)}>{e.transactionReference}</button>
                        : '—'}</td>
                      <td><code className="mco-dim">{e.requestId ?? '—'}</code></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {next && (
              <button type="button" className="mco-btn" disabled={more} onClick={() => load(applied, next)}>
                <Busy busy={more}>Load more</Busy>
              </button>
            )}
          </>
        )}
      </Panel>
    </div>
  );
}
